import Microgame from '../engine/Microgame.js';

export default class PoolGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: 'Billard !', en: 'Billiards!', es: '¡Billar!' },
            controls: { fr: 'Maintiens pour viser, relâche pour tirer', en: 'Hold to aim, release to shoot', es: 'Mantén para apuntar, suelta para disparar' },
            objective: { fr: 'Mets la boule rouge dans le trou vert', en: 'Pocket the red ball in the green hole', es: 'Mete la bola roja en el agujero verde' },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="3"></circle><line x1="12" y1="22" x2="12" y2="15"></line></svg>`,
            actions: [
                { type: 'wait', duration: 320 },
                { type: 'hold', x: 50, y: 74, holdDuration: 260 },
                { type: 'tap', x: 50, y: 20, duration: 200 },
                { type: 'wait', duration: 1100 },
            ],
            loopDuration: 3200
        };
    }
    constructor(engine) {
        super(engine);
        this.canvas = null;
        this.ctx = null;

        this.shotTaken = false;
        this.isDragging = false;

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.5;
        this.duration = 3600 * (speedMult / adj); // Reduced by 20%

        this.hasCollided = false;

        // Shot power scales with game speed so it feels punchy at high levels
        this.shotPower = 6 * (1 + (speedMult - 1) * 0.7);

        this._isAnimating = false;
        this._frozenTimerValue = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "SHOOT!";

        const W = this.container.clientWidth || 400;
        const H = this.container.clientHeight || 700;

        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.width = W;
        this.canvas.height = H;
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        // Table dimensions
        this.tableMargin = 30;
        this.tableX = this.tableMargin;
        this.tableY = H * 0.12;
        this.tableW = W - this.tableMargin * 2;
        this.tableH = H * 0.76;

        // Pocket positions (corners + midpoints)
        this.pocketRadius = 24; // Increased from 18 to make it easier
        this.pockets = [
            { x: this.tableX, y: this.tableY },
            { x: this.tableX + this.tableW, y: this.tableY },
            { x: this.tableX, y: this.tableY + this.tableH / 2 },
            { x: this.tableX + this.tableW, y: this.tableY + this.tableH / 2 },
            { x: this.tableX, y: this.tableY + this.tableH },
            { x: this.tableX + this.tableW, y: this.tableY + this.tableH },
        ];

        // Pick a random pocket for the target ball to sit near
        const targetPocket = this.isPlayback
            ? this.pockets[1]
            : this.pockets[Math.floor(Math.random() * this.pockets.length)];

        // Place cue ball in center of table first
        const cueX = this.tableX + this.tableW / 2;
        const cueY = this.tableY + this.tableH * 0.55;

        // Place target ball on the direct path between cue ball and the pocket
        const dx = targetPocket.x - cueX;
        const dy = targetPocket.y - cueY;
        const distToPocket = Math.hypot(dx, dy);
        
        // Place it 60px away from the pocket along that direct line
        const targetX = targetPocket.x - (dx / distToPocket) * 60;
        const targetY = targetPocket.y - (dy / distToPocket) * 60;

        this.targetBall = {
            x: targetX,
            y: targetY,
            vx: 0, vy: 0,
            radius: 14,
            active: true,
            pocketed: false,
        };
        this.targetPocket = targetPocket;

        this.cueBall = {
            x: cueX,
            y: cueY,
            vx: 0, vy: 0,
            radius: 14,
            active: true,
            pocketed: false,
        };

        // Cue stick angle: starts at a random angle so the player must aim
        this.cueAngle = this.isPlayback ? (-Math.PI / 2) : (Math.random() * Math.PI * 2);
        this.cueLength = 120;

        // Input: hold to aim, release to shoot
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
        this.engine.input.element.addEventListener('pointercancel', this.onUp);

        this.lastTime = null;
        this.animate();
    }

    onDown(e) {
        if (this.shotTaken || this.won || this.lost) return;
        this.isDragging = true;
        this.updateAim(e);
    }

    onMove(e) {
        if (!this.isDragging || this.shotTaken || this.won || this.lost) return;
        this.updateAim(e);
    }

    updateAim(e) {
        // Calculate angle from cue ball to pointer position
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;
        const pointerX = (e.clientX - rect.left) * scaleX;
        const pointerY = (e.clientY - rect.top) * scaleY;

        // Angle from cue ball TO pointer = direction the ball will go
        this.cueAngle = Math.atan2(
            pointerY - this.cueBall.y,
            pointerX - this.cueBall.x
        );
    }

    onUp() {
        if (!this.isDragging || this.shotTaken) return;
        this.isDragging = false;

        // SHOOT!
        this.shotTaken = true;

        // Freeze timer immediately
        this._isAnimating = true;
        this._frozenTimerValue = this.engine.timer;

        // Fire cue ball in the aimed direction
        this.cueBall.vx = Math.cos(this.cueAngle) * this.shotPower;
        this.cueBall.vy = Math.sin(this.cueAngle) * this.shotPower;
    }

    animate() {
        if (!this.active) return;

        // Freeze timer during result animation
        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        }

        const now = performance.now();
        if (!this.lastTime) this.lastTime = now;
        const dt = Math.min((now - this.lastTime) / 16.67, 3);
        this.lastTime = now;

        // Physics update
        if (this.shotTaken) {
            this.updatePhysics(dt);
        }

        this.draw();
        requestAnimationFrame(() => this.animate());
    }

    updatePhysics(dt) {
        const friction = 0.985;
        const balls = [this.cueBall, this.targetBall];

        balls.forEach(b => {
            if (!b.active) return;
            b.x += b.vx * dt;
            b.y += b.vy * dt;
            b.vx *= friction;
            b.vy *= friction;
            if (Math.abs(b.vx) < 0.05) b.vx = 0;
            if (Math.abs(b.vy) < 0.05) b.vy = 0;

            // Wall bounces
            if (b.x - b.radius < this.tableX) { b.x = this.tableX + b.radius; b.vx *= -0.7; }
            if (b.x + b.radius > this.tableX + this.tableW) { b.x = this.tableX + this.tableW - b.radius; b.vx *= -0.7; }
            if (b.y - b.radius < this.tableY) { b.y = this.tableY + b.radius; b.vy *= -0.7; }
            if (b.y + b.radius > this.tableY + this.tableH) { b.y = this.tableY + this.tableH - b.radius; b.vy *= -0.7; }
        });

        // Ball-ball collision
        if (this.cueBall.active && this.targetBall.active) {
            const dx = this.targetBall.x - this.cueBall.x;
            const dy = this.targetBall.y - this.cueBall.y;
            const dist = Math.hypot(dx, dy);
            const minDist = this.cueBall.radius + this.targetBall.radius;

            if (dist < minDist && dist > 0) {
                if (!this.hasCollided) {
                    this.hasCollided = true;
                    // Max 1 second after impact to pocket the ball, else it's a loss
                    setTimeout(() => {
                        if (!this.won && !this.lost && !this._isAnimating) {
                            this._isAnimating = true;
                            this._frozenTimerValue = this.engine.timer;
                            setTimeout(() => this.lose(), 100);
                        }
                    }, 1000);
                }

                const nx = dx / dist;
                const ny = dy / dist;
                const dvx = this.cueBall.vx - this.targetBall.vx;
                const dvy = this.cueBall.vy - this.targetBall.vy;
                const dvDotN = dvx * nx + dvy * ny;

                if (dvDotN > 0) {
                    this.cueBall.vx -= dvDotN * nx;
                    this.cueBall.vy -= dvDotN * ny;
                    this.targetBall.vx += dvDotN * nx;
                    this.targetBall.vy += dvDotN * ny;
                }

                const overlap = minDist - dist;
                this.cueBall.x -= nx * overlap * 0.5;
                this.cueBall.y -= ny * overlap * 0.5;
                this.targetBall.x += nx * overlap * 0.5;
                this.targetBall.y += ny * overlap * 0.5;
            }
        }

        // Pocket detection
        balls.forEach(b => {
            if (!b.active || b.pocketed) return;
            this.pockets.forEach(p => {
                const dist = Math.hypot(b.x - p.x, b.y - p.y);
                if (dist < this.pocketRadius + 5) {
                    b.pocketed = true;
                    b.active = false;
                    if (b === this.targetBall) {
                        setTimeout(() => this.win(), 200);
                    } else if (b === this.cueBall) {
                        setTimeout(() => this.lose(), 200);
                    }
                }
            });
        });

        // Check if both balls stopped and nothing pocketed
        const allStopped = balls.every(b => !b.active || (Math.abs(b.vx) < 0.05 && Math.abs(b.vy) < 0.05));
        if (allStopped && this.shotTaken && !this.won && !this.lost) {
            if (!this.targetBall.pocketed && !this.cueBall.pocketed) {
                setTimeout(() => this.lose(), 300);
            }
        }
    }

    draw() {
        const ctx = this.ctx;
        const W = this.canvas.width;
        const H = this.canvas.height;
        ctx.clearRect(0, 0, W, H);

        // === TABLE ===
        // Table shadow
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.beginPath();
        ctx.roundRect(this.tableX + 4, this.tableY + 4, this.tableW, this.tableH, 16);
        ctx.fill();

        // Table felt (primaryColor)
        ctx.fillStyle = this.primaryColor;
        ctx.beginPath();
        ctx.roundRect(this.tableX, this.tableY, this.tableW, this.tableH, 16);
        ctx.fill();

        // Table border (secondaryColor glow)
        ctx.strokeStyle = this.secondaryColor;
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.roundRect(this.tableX, this.tableY, this.tableW, this.tableH, 16);
        ctx.stroke();

        // Inner rail line
        ctx.strokeStyle = `${this.secondaryColor}33`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(this.tableX + 10, this.tableY + 10, this.tableW - 20, this.tableH - 20, 10);
        ctx.stroke();

        // === POCKETS ===
        this.pockets.forEach(p => {
            ctx.beginPath();
            ctx.arc(p.x, p.y, this.pocketRadius, 0, Math.PI * 2);
            ctx.fillStyle = '#111';
            ctx.fill();
            ctx.strokeStyle = '#333';
            ctx.lineWidth = 2;
            ctx.stroke();
        });

        // === POCKET GLOW for target pocket ===
        const tp = this.targetPocket;
        ctx.beginPath();
        ctx.arc(tp.x, tp.y, this.pocketRadius + 6, 0, Math.PI * 2);
        const pulseIntensity = 0.3 + 0.2 * Math.sin(performance.now() / 300);
        ctx.strokeStyle = this.secondaryColor;
        ctx.lineWidth = 3;
        ctx.globalAlpha = pulseIntensity;
        ctx.stroke();
        ctx.globalAlpha = 1;

        // === GUIDE LINE (while aiming) ===
        if (!this.shotTaken) {
            const guideEndX = this.cueBall.x + Math.cos(this.cueAngle) * 400;
            const guideEndY = this.cueBall.y + Math.sin(this.cueAngle) * 400;
            ctx.setLineDash([6, 8]);
            ctx.strokeStyle = `${this.secondaryColor}66`;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(this.cueBall.x, this.cueBall.y);
            ctx.lineTo(guideEndX, guideEndY);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        // === TARGET BALL (secondaryColor) ===
        if (this.targetBall.active) {
            this.drawBall(this.targetBall.x, this.targetBall.y, this.targetBall.radius, this.secondaryColor);
        }

        // === CUE BALL (primaryColor tinted white) ===
        if (this.cueBall.active) {
            this.drawBall(this.cueBall.x, this.cueBall.y, this.cueBall.radius, '#ffffff');
        }

        // === CUE STICK (while aiming or just holding) ===
        if (!this.shotTaken) {
            const stickOffset = this.cueBall.radius + 10;
            const stickStartX = this.cueBall.x - Math.cos(this.cueAngle) * stickOffset;
            const stickStartY = this.cueBall.y - Math.sin(this.cueAngle) * stickOffset;
            const stickEndX = stickStartX - Math.cos(this.cueAngle) * this.cueLength;
            const stickEndY = stickStartY - Math.sin(this.cueAngle) * this.cueLength;

            // Stick shadow
            ctx.strokeStyle = 'rgba(0,0,0,0.3)';
            ctx.lineWidth = 10;
            ctx.beginPath();
            ctx.moveTo(stickStartX + 2, stickStartY + 2);
            ctx.lineTo(stickEndX + 2, stickEndY + 2);
            ctx.stroke();

            // Stick body (gradient using secondaryColor)
            const grad = ctx.createLinearGradient(stickStartX, stickStartY, stickEndX, stickEndY);
            grad.addColorStop(0, this.secondaryColor);
            grad.addColorStop(1, this.primaryColor);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 7;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(stickStartX, stickStartY);
            ctx.lineTo(stickEndX, stickEndY);
            ctx.stroke();

            // Tip (white circle)
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(stickStartX, stickStartY, 4, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    drawBall(x, y, r, color) {
        const ctx = this.ctx;

        // Ball shadow
        ctx.beginPath();
        ctx.arc(x + 2, y + 3, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.fill();

        // Ball body
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.1, x, y, r);
        grad.addColorStop(0, '#fff');
        grad.addColorStop(0.4, color);
        grad.addColorStop(1, this.darkenColor(color, 0.5));
        ctx.fillStyle = grad;
        ctx.fill();

        // Specular highlight
        ctx.beginPath();
        ctx.arc(x - r * 0.25, y - r * 0.25, r * 0.35, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.fill();
    }

    darkenColor(hex, factor) {
        let r, g, b;
        if (hex.startsWith('#')) {
            const bigint = parseInt(hex.slice(1), 16);
            r = (bigint >> 16) & 255;
            g = (bigint >> 8) & 255;
            b = bigint & 255;
        } else {
            return hex;
        }
        r = Math.floor(r * factor);
        g = Math.floor(g * factor);
        b = Math.floor(b * factor);
        return `rgb(${r},${g},${b})`;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
        this.engine.input.element.removeEventListener('pointercancel', this.onUp);
    }
}
